/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件（メンズのプロダクトバックログ）の一覧ページ。全社スプシの開発タブを読み、アサインに近い順の区分ごとに案件の札（題名・キー・Backlog の状態・PL）を並べ、見るだけの区分は畳み、ずれ・区切りの形の変化・読めないことを帯で知らせる。動かす人は札をドラッグか移動メニューで並べ替えた下書きを作り、下の帯から全社スプシへ反映・元に戻すができ、動かした記録を小窓で見る。キーで開く URL では、その札に印を付けて見える所まで送る
 * @graph-connects useProductBacklog [calls] 案件一覧の取得
 * @graph-connects useProductBacklogDraft [calls] 下書きの取得
 * @graph-connects useSaveProductBacklogDraft [calls] 下書きの保存
 * @graph-connects useApplyProductBacklog [calls] 反映
 * @graph-connects useUndoProductBacklog [calls] 元に戻す
 * @graph-connects useProductBacklogMoves [calls] 動かした記録
 * @graph-connects ProductBacklogAlerts [calls] 上の帯
 * @graph-connects ProductBacklogShelf [calls] 区分
 * @graph-connects ProductBacklogBand [calls] 下の帯
 * @graph-connects ProductBacklogMovesModal [calls] 動かした記録の小窓
 */
import {
  PRODUCT_BACKLOG_MOVE_TARGETS,
  PRODUCT_BACKLOG_WRITE_PERMISSION,
  canMoveCard,
  findWriteBlock,
  menuTargetsOf,
  type ProductBacklogCard,
  type ProductBacklogSection,
  type ProductBacklogSectionKind,
} from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useParams } from '@tanstack/react-router';
import { ExternalLink, History, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductBacklogAlerts } from '../components/product-backlog/ProductBacklogAlerts.js';
import {
  ProductBacklogBand,
  type BandResult,
} from '../components/product-backlog/ProductBacklogBand.js';
import type { CardArrangeProps } from '../components/product-backlog/ProductBacklogCard.js';
import { ProductBacklogMovesModal } from '../components/product-backlog/ProductBacklogMovesModal.js';
import {
  ProductBacklogShelf,
  type ShelfArrangeProps,
} from '../components/product-backlog/ProductBacklogShelf.js';
import { useProductBacklog } from '../hooks/useProductBacklog.js';
import {
  useApplyProductBacklog,
  useDiscardProductBacklogDraft,
  useProductBacklogDraft,
  useProductBacklogMoves,
  useSaveProductBacklogDraft,
  useUndoProductBacklog,
} from '../hooks/useProductBacklogWrite.js';
import { usePermissions } from '../hooks/usePermissions.js';
import { backlogUrl } from '../lib/backlog-url.js';
import {
  arrangedSections,
  baseArrangement,
  draftSectionsOf,
  moveCard,
  overlayDraft,
  pendingMoves,
  type Arrangement,
  type PendingMove,
} from '../lib/product-backlog-arrange.js';
import { formatReadAt, visibleSections } from '../lib/product-backlog-view.js';

/**
 * 反映しない時間帯の表示を進める間隔（「HH:50 から」と押せる・押せないを、時刻が進んだら変える）。
 * @graph-connects none
 */
const CLOCK_TICK_MS = 30_000;

/**
 * 移動メニューで動かした札を光らせる時間。
 * @graph-connects none
 */
const FLASH_MS = 1600;

/**
 * 今の時刻（CLOCK_TICK_MS ごとに進む）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 反映しない時間帯の表示を進めるため、画面の今の時刻を一定の間隔で進める
 * @graph-connects none
 */
function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, CLOCK_TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return now;
}

/**
 * スプシを開く URL。
 * @graph-connects none
 */
function sheetUrlOf(spreadsheetId: string): string {
  return `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
}

/**
 * 札の印（動かした 12→3 か、元の区分 → ここへ）。
 * @graph-connects none
 */
function markOf(
  t: (key: string, params?: Record<string, string>) => string,
  move: PendingMove | undefined,
): string | null {
  if (move === undefined) return null;
  if (move.from.kind === move.to.kind) {
    return t('productBacklog.card.movedWithin', {
      from: String(move.from.rank),
      to: String(move.to.rank),
    });
  }
  return t('productBacklog.card.movedFrom', {
    section:
      move.from.kind === 'unassigned'
        ? t('productBacklog.band.unassigned')
        : t(`productBacklog.sections.${move.from.kind}.name`),
  });
}

/**
 * つかんでいる札（行番号と元の区分）と、いま落とす位置（index が null なら末尾）。
 * @graph-connects none
 */
interface DragState {
  rowNumber: number;
  from: ProductBacklogSectionKind;
  hint: { kind: ProductBacklogSectionKind; index: number | null } | null;
}

/**
 * 案件の一覧（開発）。focusKey はキーで開いたときの案件のキー。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧を、見出し（スプシを読んだ時刻・最新を読み込む・動かした記録）・上の帯・並べる区分・見るだけの区分（畳む）・下の帯で出し、動かす人には札のドラッグと移動メニューで下書きを作らせて反映・元に戻すを受け付ける
 * @graph-connects useProductBacklog [calls] 案件一覧の取得
 * @graph-connects usePermissions [calls] 動かす権限
 * @graph-connects useProductBacklogDraft [calls] 下書き
 * @graph-connects useSaveProductBacklogDraft [calls] 下書きの保存
 * @graph-connects useDiscardProductBacklogDraft [calls] 下書きを捨てる
 * @graph-connects useApplyProductBacklog [calls] 反映
 * @graph-connects useUndoProductBacklog [calls] 元に戻す
 * @graph-connects useProductBacklogMoves [calls] 動かした記録
 * @graph-connects useClock [calls] 今の時刻
 * @graph-connects overlayDraft [calls] 保存した下書きを当てる
 * @graph-connects moveCard [calls] 札を動かす
 * @graph-connects pendingMoves [calls] 未反映の並べ替え
 * @graph-connects findWriteBlock [calls] 反映しない時間帯
 * @graph-connects ProductBacklogAlerts [calls] 上の帯
 * @graph-connects ProductBacklogShelf [calls] 区分
 * @graph-connects ProductBacklogBand [calls] 下の帯
 * @graph-connects ProductBacklogMovesModal [calls] 動かした記録の小窓
 */
export function ProductBacklogPage({ focusKey = null }: { focusKey?: string | null }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, isFetching, refetch } = useProductBacklog();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canWrite = !permissionsLoading && hasPermission(PRODUCT_BACKLOG_WRITE_PERMISSION);
  const draftQuery = useProductBacklogDraft(canWrite);
  const saveDraft = useSaveProductBacklogDraft();
  const discardDraft = useDiscardProductBacklogDraft();
  const apply = useApplyProductBacklog();
  const undo = useUndoProductBacklog();
  const [recordsOpen, setRecordsOpen] = useState(false);
  const moves = useProductBacklogMoves(recordsOpen);
  const [openedKinds, setOpenedKinds] = useState<ReadonlySet<string>>(new Set());
  const [local, setLocal] = useState<{ readAt: string; arrangement: Arrangement } | null>(null);
  const [result, setResult] = useState<BandResult | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [flashRow, setFlashRow] = useState<number | null>(null);
  const now = useClock();

  useEffect(() => {
    if (flashRow === null) return undefined;
    const timer = setTimeout(() => {
      setFlashRow(null);
    }, FLASH_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [flashRow]);

  const okData = data?.sheetStatus === 'ok' ? data : null;
  const base: Arrangement = okData === null ? new Map() : baseArrangement(okData.sections);
  const serverDraft = canWrite ? (draftQuery.data?.draft ?? null) : null;
  const overlay = okData === null || serverDraft === null ? null : overlayDraft(base, serverDraft);
  const arrangement =
    (okData !== null && local?.readAt === okData.readAt ? local.arrangement : null) ??
    (overlay?.status === 'ok' ? overlay.arrangement : base);
  const arrangeEnabled =
    canWrite &&
    okData !== null &&
    okData.writeSwitch === 'on' &&
    okData.layoutIssues.length === 0 &&
    overlay?.status !== 'stale';
  const pending = okData === null ? [] : pendingMoves(base, arrangement);
  const pendingByRow = new Map(pending.map((move) => [move.card.rowNumber, move]));
  const reload = () => {
    void refetch();
  };

  const shownSections: ProductBacklogSection[] | null =
    okData === null ? null : arrangedSections(okData.sections, arrangement);
  const sections = shownSections === null ? null : visibleSections(shownSections);
  const focusShelf =
    sections === null || focusKey === null
      ? null
      : ([...sections.arrange, ...sections.viewOnly].find((s) =>
          s.cards.some((c) => c.key === focusKey),
        ) ?? null);

  useEffect(() => {
    if (focusKey === null || focusShelf === null) return;
    document
      .querySelector(`[data-key="${CSS.escape(focusKey)}"]`)
      ?.scrollIntoView({ block: 'center' });
  }, [focusKey, focusShelf]);

  const toggle = (kind: string) => {
    setOpenedKinds((current) => {
      const next = new Set(current);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  /** 札を動かして下書きにする（スプシは変えない）。 */
  const move = (rowNumber: number, to: ProductBacklogSectionKind, index: number | null) => {
    if (okData === null) return;
    const next = moveCard(arrangement, rowNumber, to, index);
    if (next === null) return;
    setLocal({ readAt: okData.readAt, arrangement: next });
    setResult(null);
    saveDraft.mutate({
      baseReadAt: okData.readAt,
      sections: draftSectionsOf(okData.sections, next),
    });
  };

  const dropAt = (kind: ProductBacklogSectionKind, index: number | null) => {
    if (drag === null) return;
    // 同じ区分の下へ動かすときは、つかんだ札を抜いた後の位置にする
    const cards = arrangement.get(kind) ?? [];
    const from = cards.findIndex((card) => card.rowNumber === drag.rowNumber);
    const adjusted = index !== null && from !== -1 && from < index ? index - 1 : index;
    setDrag(null);
    move(drag.rowNumber, kind, adjusted);
  };

  const cardArrange =
    (section: ProductBacklogSection) =>
    (card: ProductBacklogCard): CardArrangeProps => {
      const index = section.cards.findIndex((c) => c.rowNumber === card.rowNumber);
      const accepts = drag !== null && canMoveCard(drag.from, section.kind);
      const hinted = drag?.hint?.kind === section.kind ? drag.hint.index : undefined;
      return {
        movable: card.lockReason === null && PRODUCT_BACKLOG_MOVE_TARGETS[section.kind].length > 0,
        acceptsDrop: accepts,
        lockReason: card.lockReason,
        menuTargets: card.lockReason === null ? menuTargetsOf(section.kind) : [],
        mark: markOf(t, pendingByRow.get(card.rowNumber)),
        flash: flashRow === card.rowNumber,
        dropPosition: !accepts
          ? null
          : hinted === index
            ? 'before'
            : hinted === index + 1
              ? 'after'
              : null,
        onDragStart: () => {
          setDrag({ rowNumber: card.rowNumber, from: section.kind, hint: null });
        },
        onDragEnd: () => {
          setDrag(null);
        },
        onDragOver: (position) => {
          setDrag((current) =>
            current === null
              ? null
              : {
                  ...current,
                  hint: { kind: section.kind, index: position === 'before' ? index : index + 1 },
                },
          );
        },
        onDrop: () => {
          dropAt(section.kind, hinted === undefined ? index : hinted);
        },
        onMenuMove: (to) => {
          move(card.rowNumber, to, null);
          setFlashRow(card.rowNumber);
        },
      };
    };

  const shelfArrange = (section: ProductBacklogSection): ShelfArrangeProps | undefined => {
    if (!arrangeEnabled || !section.arrangeable) return undefined;
    return {
      dropState: drag === null ? null : canMoveCard(drag.from, section.kind) ? 'ok' : 'no',
      dropAtEnd: drag?.hint?.kind === section.kind && drag.hint.index === null,
      emptyDroppable: section.kind !== 'dev_waiting' && section.kind !== 'unassigned',
      cardArrange: cardArrange(section),
      onDragOverEnd: () => {
        setDrag((current) =>
          current === null ? null : { ...current, hint: { kind: section.kind, index: null } },
        );
      },
      onDropEnd: () => {
        dropAt(section.kind, null);
      },
    };
  };

  const draftUpdatedAt = serverDraft?.updatedAt ?? null;
  const onApply = () => {
    if (draftUpdatedAt === null) return;
    apply.mutate(draftUpdatedAt, {
      onSuccess: (applied) => {
        // 反映の後は一覧と下書きを読み直すので、画面だけの並びは捨てて読み直した並び（と残った下書き）を出す
        setResult({ action: 'apply', result: applied });
        setLocal(null);
      },
      onError: () => {
        setResult({ action: 'failed' });
      },
    });
  };
  const onUndo = (batchId: string) => {
    undo.mutate(batchId, {
      onSuccess: (undone) => {
        setResult({ action: 'undo', result: undone });
        setRecordsOpen(false);
      },
      onError: () => {
        setResult({ action: 'failed' });
      },
    });
  };
  const onDiscard = () => {
    if (okData !== null) setLocal({ readAt: okData.readAt, arrangement: base });
    setResult(null);
    discardDraft.mutate();
  };
  const onReloadFromBand = () => {
    setResult(null);
    setLocal(null);
    if (overlay?.status === 'stale') discardDraft.mutate();
    reload();
  };
  const titleOfKey = (key: string): string => {
    for (const cards of base.values()) {
      const found = cards.find((card) => card.key === key);
      if (found !== undefined) return found.title ?? key;
    }
    return key;
  };

  return (
    <div className="max-w-[1000px]">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
          {t('productBacklog.title')}
        </h1>
        <span className="inline-flex items-center gap-3 text-xs text-[var(--color-text-muted)]">
          <span className="inline-flex items-center gap-1.5">
            {data !== undefined && (
              <span className="tabular-nums">
                {t('productBacklog.readAt', { time: formatReadAt(data.readAt, now) })}
              </span>
            )}
            <button
              type="button"
              onClick={reload}
              disabled={isFetching}
              title={t('productBacklog.reload')}
              aria-label={t('productBacklog.reload')}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
            </button>
          </span>
          <button
            type="button"
            onClick={() => {
              setRecordsOpen(true);
            }}
            className="inline-flex items-center gap-1 text-[13px] font-medium text-[var(--color-accent-primary)] hover:underline"
          >
            <History className="h-3.5 w-3.5" aria-hidden="true" />
            {t('productBacklog.records.open')}
          </button>
        </span>
      </div>

      {isLoading && (
        <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('productBacklog.loading')}
        </p>
      )}
      {isError && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
        >
          <span>{t('productBacklog.loadError')}</span>
          <Button size="sm" variant="secondary" onClick={reload} disabled={isFetching}>
            {t('productBacklog.reload')}
          </Button>
        </div>
      )}

      {data !== undefined && (
        <ProductBacklogAlerts data={data} now={now} onReload={reload} reloading={isFetching} />
      )}

      {okData !== null && sections !== null && (
        <>
          {focusKey !== null && focusShelf === null && (
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
              <span>{t('productBacklog.notInList', { key: focusKey })}</span>
              <a
                href={backlogUrl(focusKey)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-[var(--color-accent-primary)] hover:underline"
              >
                {t('productBacklog.openBacklog')}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          )}
          <div className="flex flex-col gap-3 pb-16">
            {sections.arrange.map((section) => (
              <ProductBacklogShelf
                key={`${section.kind}-${String(section.separatorRowNumber)}`}
                section={section}
                backlogStatus={okData.backlogStatus}
                focusKey={focusKey}
                collapsible={false}
                open
                arrange={shelfArrange(section)}
              />
            ))}
            {sections.viewOnly.length > 0 && (
              <div
                role="separator"
                className="mx-0.5 mt-2.5 flex items-center gap-2.5 text-xs text-[var(--color-text-muted)] before:h-px before:flex-1 before:bg-[var(--color-border)] after:h-px after:flex-1 after:bg-[var(--color-border)]"
              >
                {t('productBacklog.viewOnlyLine')}
              </div>
            )}
            {sections.viewOnly.map((section) => {
              const id = `${section.kind}-${String(section.separatorRowNumber)}`;
              return (
                <ProductBacklogShelf
                  key={id}
                  section={section}
                  backlogStatus={okData.backlogStatus}
                  focusKey={focusKey}
                  collapsible
                  open={openedKinds.has(id) !== (focusShelf === section)}
                  onToggle={() => {
                    toggle(id);
                  }}
                />
              );
            })}
          </div>
          {canWrite && (
            <ProductBacklogBand
              moves={pending}
              draftSince={serverDraft?.createdAt ?? null}
              block={findWriteBlock(now, okData.writeWindows)}
              switchOn={okData.writeSwitch === 'on'}
              draftStale={
                overlay?.status === 'stale'
                  ? {
                      title: overlay.changed[0] === undefined ? '' : titleOfKey(overlay.changed[0]),
                      more: Math.max(0, overlay.changed.length - 1),
                    }
                  : null
              }
              working={apply.isPending || undo.isPending}
              saving={saveDraft.isPending}
              result={result}
              now={now}
              sheetUrl={sheetUrlOf(okData.spreadsheetId)}
              onApply={onApply}
              onDiscard={onDiscard}
              onUndo={onUndo}
              onOpenRecords={() => {
                setRecordsOpen(true);
              }}
              onReload={onReloadFromBand}
              onCloseResult={() => {
                setResult(null);
              }}
            />
          )}
        </>
      )}
      <ProductBacklogMovesModal
        open={recordsOpen}
        onClose={() => {
          setRecordsOpen(false);
        }}
        data={moves.data}
        loading={moves.isLoading}
        failed={moves.isError}
        canUndo={canWrite}
        undoing={undo.isPending}
        onUndo={onUndo}
      />
    </div>
  );
}

/**
 * キーで開く URL（`/backlog/{キー}`）。一覧を出し、そのキーの札に印を付ける。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理などから Backlog キーで案件を開く URL で、案件一覧をそのキーの札に印を付けて出す
 * @graph-connects ProductBacklogPage [calls] 案件一覧
 */
export function ProductBacklogKeyPage() {
  const { key } = useParams({ from: '/backlog/$key' });
  return <ProductBacklogPage focusKey={key.trim().toUpperCase()} />;
}
